<template>
  <b-modal
    id="modal-order-copy"
    ref="modal"
    :title="'copy_order' | i18n"
    :ok-title="'copy' | i18n"
    :cancel-title="'abort' | i18n"
    ok-variant="primary"
    :ok-disabled="!selectedCustomer"
    cancel-variant="outline-primary"
    size="xl"
    button-size="sm"
    @ok="copyOrderToCustomer"
    @hidden="resetModal"
  >
    <div class="row">
      <div class="col-12">
        <p>{{ 'copy_order_instructions' | i18n }}</p>
      </div>
    </div>
    <div class="row">
      <div class="col-12">
        <b-table
          id="customer-list"
          table-class="table-filtered"
          :fields="fields"
          :items="customers"
          :responsive="true"
          :busy="isLoading"
          :current-page="currentPage"
          :per-page="perPage"
          :filter-function="filterRows"
          :filter="filter"
          :sticky-header="true"
          fixed
          @filtered="onFiltered"
        >
          <template v-slot:head(customerNumber)="row">
            {{ row.label }}
            <table-filter
              v-model="filter.customerNumber"
              :placeholder="'account' | i18n"
            />
          </template>
          <template v-slot:head(name1)="row">
            {{ row.label }}
            <table-filter
              v-model="filter.name1"
              :placeholder="'name' | i18n"
            />
          </template>
          <template v-slot:head(street)="row">
            {{ row.label }}
            <table-filter
              v-model="filter.street"
              :placeholder="'street_short' | i18n"
            />
          </template>
          <template v-slot:head(zip)="row">
            {{ row.label }}
            <table-filter
              v-model="filter.zip"
              :placeholder="'zip_short' | i18n"
            />
          </template>
          <template v-slot:head(city)="row">
            {{ row.label }}
            <table-filter
              v-model="filter.city"
              :placeholder="'city' | i18n"
            />
          </template>
          <template v-slot:cell(check)="row">
            <b-form-checkbox
              :id="'checkbox__' + row.item.id"
              v-model="row.item.isSelected"
              :checked="row.item.isSelected"
              :name="'checkbox__' + row.item.id"
              @change="setSelectedCustomer(row.item)"
            />
          </template>
          <template v-slot:table-busy>
            <div class="text-center">
              <b-spinner class="align-middle" />
              <strong class="ml-1">{{ 'loading' | i18n }}...</strong>
            </div>
          </template>
        </b-table>
        <b-pagination
          v-model="currentPage"
          class="table-pagination"
          :total-rows="totalRows"
          :per-page="perPage"
          size="sm"
        />
      </div>
    </div>
  </b-modal>
</template>

<script>
import {NotificationMixin, CustomerMixin, OrderMixin, TableFilterMixin, ScreenSizeMixin} from '../../mixins';
import TableFilter from '../table/table-filter';
import {BTable} from 'bootstrap-vue';

export default {
  name: 'CopyOrder',
  components: {TableFilter, 'b-table': BTable},
  mixins: [NotificationMixin, CustomerMixin, OrderMixin, TableFilterMixin, ScreenSizeMixin],
  props: {
    orderId: {
      type: String,
      default: null
    }
  },
  data: function () {
    return {
      fields: [
        {key: 'check', label: '', thClass: 'check'},
        {key: 'customerNumber', label: this.$translate('account'), thClass: 'customer-number'},
        {key: 'name1', label: this.$translate('name'), thClass: 'name1'},
        {key: 'street', label: this.$translate('street_short'), thClass: 'street'},
        {key: 'zip', label: this.$translate('zip_short'), thClass: 'zip'},
        {key: 'city', label: this.$translate('city'), thClass: 'city'},
      ],
      customers: [],
      currentPage: 1,
      perPage: 10,
      totalRows: 0,
      isLoading: false,
      selectedCustomer: null,
      filter: {
        customerNumber: null,
        name1: null,
        street: null,
        city: null,
        zip: null
      }
    };
  },
  created() {
    if (this.isExtraLargeScreen) {
      this.perPage = 20;
    }
  },
  mounted() {
    this.$root.$on('bv::modal::show', (bvEvent, modalId) => {
      if (modalId === 'modal-order-copy') {
        this.loadCustomers();
      }
    });
  },
  methods: {
    async loadCustomers() {
      try {
        this.isLoading = true;
        const response = await this.customerService.getCustomers();
        this.customers = response.customers.map(c => ({...c, isSelected: false}));
        this.totalRows = this.customers.length;
        this.isLoading = false;
      } catch (err) {
        this.showNotification({type: 'error', message: err.message});
      }
    },
    setSelectedCustomer(item) {
      this.selectedCustomer = item;
      this.customers.forEach(customer => {
        if (customer.id !== this.selectedCustomer.id) {
          customer.isSelected = false;
        }
      });
    },
    async copyOrderToCustomer(event) {
      event.preventDefault();
      try {
        await this.orderService.copyOrderToCustomer({orderId: this.orderId, customerId: this.selectedCustomer.id});
        this.showNotification({type: 'success', title: this.$translate('copy_order'), message: this.$translate('order_copy_success')});
        this.$refs.modal.hide();
        this.$emit('copied');
      } catch (err) {
        this.showNotification({type: 'error', message: err.message});
      }
    },
    resetModal() {
      this.selectedCustomer = null;
    },
    onFiltered(filteredItems) {
      this.totalRows = filteredItems.length;
      this.currentPage = 1;
    }
  }
};
</script>

<style scoped lang="scss">
  p {
    color: #7586A5;
  }

  ::v-deep   .b-table-sticky-header {
    max-height: calc(100vh - 400px);
    width: calc(100% - 3.5px);
  }

  ::v-deep   .table {
    font-size: 0.875rem;
    line-height: 1.5rem;

    .b-table-top-row td {
      padding: 6px;
    }

    th {
      &.check {
        width: 5%;
      }

      &.customer-number {
        width: 15%;
      }

      &.name1 {
        width: 20%;
      }

      &.street {
        width: 30%;
      }

      &.zip {
        width: 10%;
      }

      &.city {
        width: 20%;
      }
    }
  }
</style>
